import { on } from "@ngrx/store";
import { SuperUserState } from "../../super-user.state";
import { ExternalUsersActions } from "./external-users.actions";
import { PagesStore } from "../../../../pages.state";

// **************************************************************************
// HELPER: Update superUser state within PagesStore
// **************************************************************************

function withSuperUser(state: PagesStore, updater: (superUser: SuperUserState) => SuperUserState): PagesStore {
    return { ...state, superUser: updater(state.superUser) };
}

// **************************************************************************
// EXTERNAL USERS REDUCER (array format for PagesStore)
// **************************************************************************

const loadExternalUsers = on(ExternalUsersActions.loadExternalUsers, (state: PagesStore) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: { ...superUserState.externalUsers, loading: true },
    error: null,
})));

const loadExternalUsersSuccess = on(ExternalUsersActions.loadExternalUsersSuccess, (state: PagesStore, { users }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: { ...superUserState.externalUsers, externalUsers: users, loaded: true, loading: false },
    error: null,
})));

const invalidate = on(ExternalUsersActions.invalidate, (state: PagesStore) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: { ...superUserState.externalUsers, loaded: false },
})));

const loadExternalUsersFailure = on(ExternalUsersActions.loadExternalUsersFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: { ...superUserState.externalUsers, loading: false },
    error,
})));

const createExternalUserSuccess = on(ExternalUsersActions.createExternalUserSuccess, (state: PagesStore, { user }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: {
        ...superUserState.externalUsers,
        externalUsers: [...superUserState.externalUsers.externalUsers, user],
    },
    error: null,
})));

const createExternalUserFailure = on(ExternalUsersActions.createExternalUserFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

const updateExternalUserSuccess = on(ExternalUsersActions.updateExternalUserSuccess, (state: PagesStore, { user }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: {
        ...superUserState.externalUsers,
        externalUsers: superUserState.externalUsers.externalUsers.map(u => u.id === user.id ? user : u),
    },
    error: null,
})));

const updateExternalUserFailure = on(ExternalUsersActions.updateExternalUserFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

const deleteExternalUserSuccess = on(ExternalUsersActions.deleteExternalUserSuccess, (state: PagesStore, { id }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: {
        ...superUserState.externalUsers,
        externalUsers: superUserState.externalUsers.externalUsers.filter(u => u.id !== id),
    },
    error: null,
})));

const deleteExternalUserFailure = on(ExternalUsersActions.deleteExternalUserFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

const addContractToExternalUserSuccess = on(ExternalUsersActions.addContractToExternalUserSuccess, (state: PagesStore, { user }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: {
        ...superUserState.externalUsers,
        externalUsers: superUserState.externalUsers.externalUsers.map(u => u.id === user.id ? user : u),
    },
    error: null,
})));

const addContractToExternalUserFailure = on(ExternalUsersActions.addContractToExternalUserFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

const removeContractFromExternalUserSuccess = on(ExternalUsersActions.removeContractFromExternalUserSuccess, (state: PagesStore, { user }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    externalUsers: {
        ...superUserState.externalUsers,
        externalUsers: superUserState.externalUsers.externalUsers.map(u => u.id === user.id ? user : u),
    },
    error: null,
})));

const removeContractFromExternalUserFailure = on(ExternalUsersActions.removeContractFromExternalUserFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

const changeExternalUserPasswordFailure = on(ExternalUsersActions.changeExternalUserPasswordFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

export const externalUsersReducer = [
    invalidate,
    loadExternalUsers,
    loadExternalUsersSuccess,
    loadExternalUsersFailure,
    createExternalUserSuccess,
    createExternalUserFailure,
    updateExternalUserSuccess,
    updateExternalUserFailure,
    deleteExternalUserSuccess,
    deleteExternalUserFailure,
    addContractToExternalUserSuccess,
    addContractToExternalUserFailure,
    removeContractFromExternalUserSuccess,
    removeContractFromExternalUserFailure,
    changeExternalUserPasswordFailure,
];
